<div
  class="psm-overlay"
  role="dialog"
  aria-modal="true"
  aria-labelledby="psm-dialog-title"
  (mousedown)="onOverlayMouseDown($event)"
>
  <div class="psm-shell" (mousedown)="$event.stopPropagation()">
    <div class="psm-main" [class.psm-main--edit-footer]="accountEditMode">
      <header class="psm-header">
        <div class="psm-header-text">
          <h2 id="psm-dialog-title" class="psm-header-title">
            {{ accountHeaderTitle() }}
          </h2>
          <p class="psm-header-subtitle">{{ accountHeaderSubtitle() }}</p>
        </div>
        <div class="psm-header-close-wrap">
          <button
            type="button"
            class="psm-close"
            aria-label="Close settings"
            (click)="close()"
          >
            <img src="assets/icons/close.svg" alt="" class="psm-close-icon" />
          </button>
        </div>
      </header>

      <div class="psm-body">
        @if (loading) {
          <div class="psm-loading">
            <app-aio-branded-loader [captionPrefix]="'Loading profile'" />
          </div>
        } @else if (error) {
          <div class="psm-error">
            <p>{{ error }}</p>
            <aio-button appearance="secondary" size="sm" label="Try again" (pressed)="loadUserProfile()" />
          </div>
        } @else if (user) {
          <div class="psm-scroll">
            <!-- Account card -->
            <section class="psm-card">
              @if (!accountEditMode) {
                <div class="psm-account-top">
                  <div class="psm-account-left">
                    @if (hasProfilePicture()) {
                      <div class="psm-avatar psm-avatar--image">
                        <img
                          [src]="user.profilePictureUrl"
                          [alt]="getFullName()"
                        />
                      </div>
                    } @else {
                      <div class="psm-avatar psm-avatar--initials">
                        {{ getUserInitials() }}
                      </div>
                    }
                    <div class="psm-account-name">{{ getFullName() }}</div>
                    <div class="psm-account-joined">
                      Joined {{ getJoinedDate() }}
                    </div>
                  </div>
                  <button
                    type="button"
                    class="psm-edit-pill"
                    (click)="enterAccountEdit()"
                  >
                    <span class="psm-edit-pill-icon-wrap" aria-hidden="true">
                      <img
                        src="assets/icons/pencil.svg"
                        alt=""
                        class="psm-edit-pill-icon"
                      />
                    </span>
                    Edit Account
                  </button>
                </div>

                <div class="psm-divider"></div>

                <div class="psm-role-block">
                  <div class="psm-field-label">Role</div>
                  <div class="psm-field-value psm-field-value--indent">
                    {{ getRoleLabel() }}
                  </div>
                </div>

                <div class="psm-phone-address">
                  <div class="psm-phone-col">
                    <div class="psm-field-label">Phone Number</div>
                    @if (getPhoneLines().length === 0) {
                      <div class="psm-field-value psm-field-value--indent">—</div>
                    } @else {
                      @for (line of getPhoneLines(); track line) {
                        <div class="psm-field-value psm-field-value--indent">
                          {{ line }}
                        </div>
                      }
                    }
                  </div>
                  <div class="psm-address-col">
                    <div class="psm-field-label">Address</div>
                    <div class="psm-field-value psm-field-value--indent">
                      {{ getAddressDisplay() }}
                    </div>
                  </div>
                </div>
              } @else {
                <form class="psm-edit-form" [formGroup]="editForm">
                  <div class="psm-edit-photo-row">
                    @if (editAvatarShowsImage()) {
                      <div class="psm-edit-avatar psm-edit-avatar--image">
                        <img
                          [src]="getEditAvatarImageSrc()"
                          [alt]="getFullName()"
                        />
                      </div>
                    } @else {
                      <div class="psm-edit-avatar psm-edit-avatar--initials">
                        {{ getUserInitials() }}
                      </div>
                    }
                    <div class="psm-edit-photo-actions">
                      <button
                        type="button"
                        class="psm-replace-image-btn"
                        [disabled]="saving"
                        (click)="onReplaceImageClick()"
                      >
                        {{ getProfileImageActionLabel() }}
                      </button>
                      @if (canRemoveProfilePicture()) {
                        <button
                          type="button"
                          class="psm-remove-image-btn"
                          [disabled]="saving"
                          (click)="onRemoveProfilePicture()"
                        >
                          Remove
                        </button>
                      }
                    </div>
                  </div>

                  <div class="psm-edit-name-role-row">
                    <aio-input-field
                      class="psm-figma-field"
                      id="psm-first-name"
                      label="First Name"
                      [required]="true"
                      formControlName="firstName"
                      placeholder="Enter first name"
                      [error]="
                        (editForm.get('firstName')?.invalid &&
                          editForm.get('firstName')?.touched) ??
                        false
                      "
                      name="psmFirstName"
                      [fullWidth]="true"
                    />
                    <aio-input-field
                      class="psm-figma-field"
                      id="psm-last-name"
                      label="Last Name"
                      [required]="true"
                      formControlName="lastName"
                      placeholder="Enter last name"
                      [error]="
                        (editForm.get('lastName')?.invalid &&
                          editForm.get('lastName')?.touched) ??
                        false
                      "
                      name="psmLastName"
                      [fullWidth]="true"
                    />
                    <div class="psm-role-edit-field">
                      <div class="psm-field-label">Role</div>
                      <div class="psm-field-value">
                        {{ getRoleLabel() }}
                      </div>
                    </div>
                  </div>

                  <div class="psm-edit-address-grid">
                    <aio-input-field
                      class="psm-figma-field psm-edit-address-full"
                      id="psm-street1"
                      label="Address Line 1"
                      formControlName="street1"
                      placeholder="Enter street address"
                      name="psmStreet1"
                      autocomplete="address-line1"
                      [fullWidth]="true"
                    />
                    <aio-input-field
                      class="psm-figma-field psm-edit-address-full"
                      id="psm-street2"
                      label="Address Line 2"
                      formControlName="street2"
                      placeholder="Apt, suite, etc. (optional)"
                      name="psmStreet2"
                      autocomplete="address-line2"
                      [fullWidth]="true"
                    />
                    <aio-input-field
                      class="psm-figma-field"
                      id="psm-city"
                      label="City"
                      formControlName="city"
                      placeholder="Enter city"
                      name="psmCity"
                      autocomplete="address-level2"
                      [fullWidth]="true"
                    />
                    <aio-input-field
                      class="psm-figma-field"
                      id="psm-state"
                      label="State"
                      formControlName="state"
                      placeholder="Enter state"
                      name="psmState"
                      autocomplete="address-level1"
                      [fullWidth]="true"
                    />
                    <aio-input-field
                      class="psm-figma-field"
                      id="psm-zip"
                      label="ZIP Code"
                      formControlName="zip"
                      placeholder="Enter ZIP code"
                      name="psmZip"
                      autocomplete="postal-code"
                      [fullWidth]="true"
                    />
                    <aio-input-field
                      class="psm-figma-field psm-edit-address-full"
                      id="psm-country"
                      label="Country"
                      formControlName="country"
                      placeholder="Enter country"
                      name="psmCountry"
                      autocomplete="country-name"
                      [fullWidth]="true"
                    />
                  </div>

                  <div class="psm-phone-slots" formArrayName="phones">
                    @for (
                      _slot of phonesArray.controls;
                      track $index;
                      let i = $index
                    ) {
                      <div class="psm-phone-slot" [formGroupName]="i">
                        <aio-select
                          class="psm-figma-field psm-phone-type-select"
                          [id]="'psm-phone-type-' + i"
                          label="Type"
                          formControlName="phoneType"
                          [options]="phoneTypeOptions"
                          placeholder="Type"
                          [fullWidth]="true"
                          [clearable]="true" />
                        <aio-input-field
                          class="psm-figma-field psm-phone-slot-input"
                          [id]="'psm-phone-' + i"
                          label="Phone Number"
                          formControlName="number"
                          type="tel"
                          [adapter]="usPhoneAdapter"
                          placeholder="(___) ___ - ____"
                          autocomplete="tel-national"
                          [error]="phoneFieldError(i)"
                          [name]="'psmPhone' + i"
                          [fullWidth]="true"
                          (focusout)="onPhoneSlotBlur(i)"
                        />
                        <aio-input-field
                          class="psm-figma-field psm-phone-slot-ext"
                          [id]="'psm-phone-ext-' + i"
                          label="Extension"
                          formControlName="extension"
                          placeholder="Ext."
                          [name]="'psmPhoneExt' + i"
                          autocomplete="tel-extension"
                          [fullWidth]="true"
                        />
                        @if (canRemovePhoneSlot()) {
                          <button
                            type="button"
                            class="psm-phone-delete-number"
                            aria-label="Delete phone number"
                            (click)="removePhoneSlot(i)"
                          >
                            Delete Number
                          </button>
                        }
                      </div>
                    }
                  </div>

                </form>
              }
            </section>

            <section class="psm-card psm-card--connected">
              <h3 class="psm-card-section-title">Connected Accounts</h3>

              @if (showNoTenantProviderMessage()) {
                <p class="psm-connected-empty">
                  No email or calendar provider is enabled for your organization. Ask an admin to
                  enable Google Workspace or Microsoft 365 under Admin → Integrations.
                </p>
              }

              @for (row of visibleIntegrationRows(); track row.key) {
                <div
                  class="psm-connected-row"
                  [class.psm-connected-row--spaced]="$index > 0">
                  <div class="psm-connected-main">
                    <div class="psm-gmail-badge">
                      <img [src]="row.iconSrc" alt="" class="psm-gmail-img" />
                    </div>
                    <div class="psm-connected-copy">
                      <div class="psm-connected-title">{{ row.title }}</div>
                      <div class="psm-connected-email">
                        {{ integrationDisplayLine(row.key) }}
                      </div>
                      <p class="psm-connected-help">{{ integrationHelpText(row.key) }}</p>
                    </div>
                  </div>
                  @if (isIntegrationRowConnected(row.key)) {
                    <button
                      type="button"
                      class="psm-disconnect"
                      [disabled]="integrationsBusy"
                      (click)="disconnectIntegration(row.key)">
                      Disconnect
                    </button>
                  } @else {
                    <button
                      type="button"
                      class="psm-connect"
                      [disabled]="integrationsBusy"
                      (click)="connectIntegration(row.key)">
                      Connect
                    </button>
                  }
                </div>
              }
            </section>

            <section class="psm-card psm-card--notifications">
              <h3 class="psm-card-section-title psm-notifications-heading">
                Notifications
              </h3>
              <div class="psm-notifications-grid">
                <div class="psm-notifications-title">Receiving notifications</div>
                <p class="psm-notifications-desc">
                  Turning this off stops email and text notifications and hides
                  badges on the bell icon. You can still view past notifications
                  anytime. Changes save immediately.
                </p>
                <div class="psm-notifications-toggle-cell">
                  <aio-toggle
                    name="psmReceivingNotifications"
                    [(ngModel)]="receivingNotificationsEnabled"
                    (ngModelChange)="onReceivingNotificationsEnabledChange($event)"
                    [disabled]="receivingPreferenceSaving"
                    inputId="psm-notify-receiving-preference"
                    class="psm-settings-toggle"
                  />
                </div>
              </div>
            </section>

            <!-- Security -->
            <section class="psm-card psm-card--security">
              <h3 class="psm-card-section-title psm-security-heading">
                Security
              </h3>
              <div class="psm-security-grid">
                <div class="psm-security-title">2-Step Verification</div>
                <p class="psm-security-desc">
                  Turn two-factor authentication on or off and receive
                  verification codes by text message or email when you sign
                  in.
                </p>
                <div class="psm-security-toggle-cell">
                  <aio-toggle
                    [(ngModel)]="twoFactorEnabled"
                    (ngModelChange)="onTwoFactorToggle($event)"
                    [disabled]="mfaToggleSaving || saving"
                    inputId="psm-2fa-toggle"
                    class="psm-settings-toggle"
                  />
                </div>
              </div>
            </section>
          </div>
        }
      </div>

      @if (accountEditMode) {
        <footer class="psm-edit-footer">
          <button
            type="button"
            class="psm-footer-cancel"
            [disabled]="saving"
            (click)="cancelAccountEdit()"
          >
            Cancel
          </button>
          <button
            type="button"
            class="psm-footer-save"
            [disabled]="saving"
            (click)="onSaveAccount()"
          >
            @if (saving) {
              Saving…
            } @else {
              Save changes
            }
          </button>
        </footer>
      }
    </div>
  </div>

  <app-upload-modal
    [isOpen]="showUploadModal"
    [resetModal]="resetUploadModal"
    (isOpenChange)="onUploadModalClosed($event)"
    (uploadSucceeded)="onProfileImageUploadSucceeded($event)"
    (fileSelected)="onUploadFileSelected($event)"
  />
</div>
